import { User } from 'configcat-react';

import { trackedEvents } from 'config/trackedEvents.config';
import { track } from 'lib/analytics';

import type { axiosParams } from './api';
import { apiCall } from './api';
import { clearLocalCredentials } from './localSession';
import { sn } from './safe';
import { store } from '../stores';

export function impersonateByID(
  localStorage: any,
  axios: (axiosParams) => Promise<*>,
  userID: string,
  accessOverride: boolean = false,
  currentEmail?: string,
): Promise<userT> {
  localStorage.setItem('impersonate', userID);
  localStorage.setItem('impersonate_access_override', accessOverride ? 'true' : 'false');

  if (accessOverride) {
    localStorage.setItem('impersonating_email', currentEmail || sn(store.getState(), 'user.user.email', ''));
  }

  return getSignedUser(localStorage, axios);
}

/**
 * Drop impersonation and reload the real user.
 *
 * Impersonation is expressed purely as request headers derived from these
 * localStorage keys - it never swapped the caller's own token - so removing
 * them is the whole operation.
 *
 * The previous implementation had a second branch that restored an
 * `old-access-token` into `access-token`. Nothing ever wrote that key, so the
 * branch was unreachable, and under Cognito it could not work anyway: the
 * access token belongs to oidc-client-ts, not to localStorage.
 */
export function unImpersonate(localStorage: any, axios: (axiosParams) => Promise<*>): Promise<userT> {
  localStorage.removeItem('impersonate');
  localStorage.removeItem('impersonate_access_override');
  localStorage.removeItem('impersonating_email');

  return getSignedUser(localStorage, axios);
}

// Registration is no longer an API call from this app.
//
// `signUp()` used to POST `/signup` with an Auth0 access token and a
// company/coupon/captcha payload collected by `/sign-up-c6`. Under the
// approved onboarding architecture registration happens at Cognito Managed
// Login and provisioning is enforced afterwards by the `/api/v2/users/me`
// readiness gate, which returns 409 HUB_REQUIRED with a hub onboarding URL
// until the user actually exists. This app runs that gate in
// `lib/readiness.js` before calling `getSignedUser`.

export function getSignedUser(localStorage: any, axios: (axiosParams) => Promise<*>): Promise<*> {
  return apiCall({
    localStorage,
    axios,
    method: 'get',
    url: '/user',
    secure: true,
  })
    .then((user: userT) => Promise.resolve(user))
    .catch((error) => {
      signOut();

      return Promise.reject(error);
    });
}

/**
 * Clears locally held credentials.
 *
 * The Cognito session itself is torn down by the `/sign-out` page, which also
 * revokes it platform-wide through hub; this only handles what is reachable
 * from plain storage. Kept as a separate function because `getSignedUser`
 * calls it on failure, where a full platform logout would be too aggressive.
 */
export function signOut(): void {
  clearLocalCredentials();

  track(trackedEvents.logout);
}

export function getFeatureFlagUser(): User {
  const state = store.getState();

  const selectedAccount = sn(state, 'accounts.selected', '');

  let email = sn(state, 'user.user.email', '');

  if (localStorage.getItem('impersonate_access_override') === 'true') {
    email = localStorage.getItem('impersonating_email') || email;
  }

  const planType = sn(state, 'subscription.sub.plan.type', '');

  return new User(selectedAccount, email, undefined, {
    plan: planType,
    domain: window.location.hostname,
    userId: state?.user?.user?.id,
  });
}
